import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Mail, Lock, User, Building, Phone, ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import BrandWordmark from "../components/BrandWordmark";
import BlueprintBackdrop from "../components/BlueprintBackdrop";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RegisterPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    company: "",
    phone: "",
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API}/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Registration failed");
      }

      localStorage.setItem("token", data.access_token);
      toast.success("Account created successfully!");
      navigate("/dashboard", { state: { user: data.user } });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex" data-testid="register-page">
      {/* LEFT — split-screen brand panel */}
      <aside className="relative hidden lg:flex lg:w-1/2 flex-col justify-between p-12 overflow-hidden border-r border-border">
        <BlueprintBackdrop variant="dense" showIsometric={true} />

        <div className="relative z-10">
          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-sm"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </button>
        </div>

        <div className="relative z-10 max-w-md">
          <div className="pre-headline-pill mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-accent" />
            Built for California contractors
          </div>
          <h1 className="font-display font-black text-5xl xl:text-6xl leading-[0.95] text-foreground tracking-tight">
            Your first plan is&nbsp;
            <span className="headline-accent">on us.</span>
          </h1>
          <p className="mt-6 text-base text-muted-foreground leading-relaxed">
            Generate your first CALGreen-compliant CWMP for ${" "}<span className="font-bold">$59</span>, or unlock unlimited with the Bundle or Subscription tier.
          </p>

          <ul className="mt-8 space-y-3 text-sm text-foreground">
            <li className="flex items-center gap-3">
              <span className="w-6 h-6 rounded-full bg-accent/12 text-accent inline-flex items-center justify-center text-xs font-bold">1</span>
              All 58 California counties supported
            </li>
            <li className="flex items-center gap-3">
              <span className="w-6 h-6 rounded-full bg-accent/12 text-accent inline-flex items-center justify-center text-xs font-bold">2</span>
              Council-ready PDFs in under 10 minutes
            </li>
            <li className="flex items-center gap-3">
              <span className="w-6 h-6 rounded-full bg-accent/12 text-accent inline-flex items-center justify-center text-xs font-bold">3</span>
              CALGreen 65% diversion calculations baked-in
            </li>
          </ul>
        </div>

        <div className="relative z-10">
          <BrandWordmark size="lg" onClick={() => navigate("/")} />
          <p className="mt-2 text-xs text-muted-foreground">
            © {new Date().getFullYear()} Plan Generator CA.
          </p>
        </div>
      </aside>

      {/* RIGHT — registration form */}
      <main className="relative flex-1 flex items-center justify-center px-4 sm:px-8 py-12 overflow-hidden">
        <BlueprintBackdrop variant="hero" showIsometric={false} />

        <div className="relative w-full max-w-md z-10 animate-fade-in-up">
          {/* Mobile-only top bar */}
          <div className="lg:hidden flex items-center justify-between mb-8">
            <BrandWordmark size="md" onClick={() => navigate("/")} />
            <button
              onClick={() => navigate("/")}
              className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            >
              <ArrowLeft className="w-4 h-4" /> Home
            </button>
          </div>

          <div className="glass-card p-8 sm:p-10" data-testid="register-card">
            <div className="mb-6">
              <span className="pre-headline-pill">Create account</span>
              <h2 className="mt-3 font-display font-black text-3xl sm:text-4xl text-foreground tracking-tight">
                Create account
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                No credit card required.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-xs font-semibold uppercase tracking-wider text-foreground mb-1.5">
                  Full name
                </label>
                <div className="relative">
                  <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="field-input pl-10"
                    data-testid="name-input"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-wider text-foreground mb-1.5">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="field-input pl-10"
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-xs font-semibold uppercase tracking-wider text-foreground mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    minLength={6}
                    className="field-input pl-10"
                    data-testid="password-input"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="company" className="block text-xs font-semibold uppercase tracking-wider text-foreground mb-1.5">
                  Company <span className="text-muted-foreground font-normal normal-case">(optional)</span>
                </label>
                <div className="relative">
                  <Building className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    id="company"
                    name="company"
                    type="text"
                    placeholder="ABC Construction Inc."
                    value={formData.company}
                    onChange={handleInputChange}
                    className="field-input pl-10"
                    data-testid="company-input"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="phone" className="block text-xs font-semibold uppercase tracking-wider text-foreground mb-1.5">
                  Phone <span className="text-muted-foreground font-normal normal-case">(optional)</span>
                </label>
                <div className="relative">
                  <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="field-input pl-10"
                    data-testid="phone-input"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="pill-btn pill-btn-primary w-full justify-center mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="submit-btn"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Creating account…
                  </>
                ) : (
                  <>
                    Create account
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            <p className="mt-8 text-center text-sm text-muted-foreground">
              Already have an account?{" "}
              <Link
                to="/login"
                className="text-accent font-semibold hover:underline"
                data-testid="login-link"
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default RegisterPage;
